import { Download, FileText } from "lucide-react";
import { useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { VerificationService } from "../services/verificationService";

export interface DocumentsReadOnlyListProps {
  identityId: string;
  /** Whether a verification document is expected (e.g. the identity is VERIFIED). */
  hasDocument: boolean;
}

/**
 * Read-only document access (post-verification). The only UI point that touches documents, and it is never an
 * upload. Downloads use a short-lived S3 signed URL fetched on demand from `GET
 * /internal/identities/:id/document`. When no document exists yet, shows an empty state.
 *
 * Note: the backend currently exposes a single signed URL (the verification report). When it exposes the full
 * extracted documents array, this list maps one item per document with the same download affordance.
 */
export function DocumentsReadOnlyList({ identityId, hasDocument }: DocumentsReadOnlyListProps) {
  const { t } = useTranslations("verification");
  const { isInternalUser } = useAuth();
  const [downloading, setDownloading] = useState(false);

  // The report can carry AML alerts and rejection grounds: Coloca staff only, whoever mounts this.
  if (!isInternalUser) return null;

  if (!hasDocument) {
    return (
      <ColocaEmpty
        icon={FileText}
        title={t("documents_empty_title")}
        description={t("documents_empty_description")}
      />
    );
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await VerificationService.getDocument(identityId);
      window.open(data.signed_url, "_blank", "noopener,noreferrer");
    } catch {
      colocaToast.error(t("error_generic_description"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-border rounded-xl border border-border">
        <li className="flex items-center justify-between gap-4 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <FileText className="size-4" aria-hidden="true" />
            </span>
            <span className="truncate text-sm text-foreground">{t("documents_report_label")}</span>
          </div>
          <ColocaButton
            variant="default"
            size="sm"
            icon={Download}
            loading={downloading}
            onClick={handleDownload}
          >
            {t("documents_download")}
          </ColocaButton>
        </li>
      </ul>
      <p className="text-xs text-muted-foreground">{t("documents_signed_note")}</p>
    </div>
  );
}
